import React, { ReactNode, useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  Chip,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import HomeIcon from "@mui/icons-material/Home";
import SchoolIcon from "@mui/icons-material/School";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { PROGRAM_COLORS } from "../constants/colors";

// Dark text on all program colors — all pass WCAG AAA (7:1+)
const PROGRAM_TEXT_COLORS: Record<string, string> = {
  EAP_YELLOW: "#3e2800",   // 7.55:1 on #f2b443
  EAP_GREEN: "#00332a",    // dark green on #78c0a1
  EAP_ACCESS: "#002b36",   // dark teal on #34c1d6
};

const getProgramThemeColor = (programName?: string): { bg: string; text: string } | null => {
  if (!programName) return null;
  const normalized = programName.toUpperCase().replace(" ", "_");
  const bg = (PROGRAM_COLORS as any)[normalized];
  if (!bg) return null;
  return { bg, text: PROGRAM_TEXT_COLORS[normalized] || "#fff" };
};

type StudentLayoutProps = {
  children: ReactNode;
};

const StudentLayout: React.FC<StudentLayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  // State for profile menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Handle profile menu open
  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle profile menu close
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Navigation handlers
  const handleProfileNav = () => {
    navigate("/student/profile");
    handleClose();
  };

  const handleClassroomNav = () => {
    navigate("/student/my-classroom");
    handleClose();
  };

  const handleDashboardNav = () => {
    navigate("/student/dashboard");
    handleClose();
  };

  const handleHelpNav = () => {
    // Could open help modal or navigate to help page
    handleClose();
  };

  // Handle logout
  const handleLogout = () => {
    signOut();
    navigate("/auth/sign-in");
    handleClose();
  };

  // Get user's initials
  const getUserInitials = () => {
    if (!user) return "?";

    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(
        0
      )}`.toUpperCase();
    }

    return user.firstName?.charAt(0).toUpperCase() || "?";
  };

  // Get program name for display
  const getProgramDisplay = () => {
    const programName = user?.myClassroom?.programName;
    if (!programName) return null;

    // Format: "EAP YELLOW" -> "Yellow"
    const formatted = programName.replace("EAP ", "").toLowerCase();
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const programDisplay = getProgramDisplay();
  const programTheme = getProgramThemeColor(user?.myClassroom?.programName);

  return (
    <>
      <AppBar
        position="static"
        elevation={2}
        sx={{
          backgroundColor: programTheme?.bg || theme.palette.primary.main,
          color: programTheme?.text || "#fff",
          marginBottom: 3,
        }}
      >
        <Toolbar>
          {/* Left side - Logo and Title */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <MenuBookIcon sx={{ mr: 1.5, fontSize: 28 }} />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: "bold",
                display: { xs: "none", sm: "block" },
              }}
            >
              EAP Space
            </Typography>

            {/* Program Badge */}
            {programDisplay && (
              <Chip
                label={programDisplay}
                size="small"
                sx={{
                  ml: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  fontWeight: 600,
                  display: { xs: "none", md: "flex" },
                }}
              />
            )}
          </Box>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Right side - Actions */}

          {/* My Classroom Button (visible on larger screens) */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              mr: 2,
              cursor: "pointer",
              padding: "6px 12px",
              borderRadius: 1,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
            onClick={handleClassroomNav}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              My Classroom
            </Typography>
          </Box>

          {/* Avatar with dropdown */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: 1,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
            onClick={handleProfileClick}
            aria-controls={open ? "profile-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <Typography
              variant="body2"
              sx={{
                mr: 1.5,
                fontWeight: 500,
                display: { xs: "none", sm: "block" },
              }}
            >
              {user?.firstName}
            </Typography>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: programTheme ? "rgba(0,0,0,0.15)" : theme.palette.secondary.main,
                color: programTheme?.text || theme.palette.secondary.contrastText,
                fontSize: "0.9rem",
                fontWeight: 600,
              }}
            >
              {getUserInitials()}
            </Avatar>
          </Box>

          {/* Profile Menu */}
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 3,
              sx: {
                width: 260,
                mt: 1.5,
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {/* User info section */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {user?.email}
              </Typography>
              {programDisplay && (
                <Chip
                  icon={<SchoolIcon sx={{ fontSize: 16 }} />}
                  label={`EAP ${programDisplay}`}
                  size="small"
                  sx={{ mt: 1 }}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            <Divider />

            {/* Menu items */}
            <MenuItem onClick={handleClassroomNav}>
              <ListItemIcon>
                <HomeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>My Classroom</ListItemText>
            </MenuItem>

            <MenuItem onClick={handleProfileNav}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>My Profile</ListItemText>
            </MenuItem>

            <Divider />

            {/* Sign out */}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: theme.palette.error.main }}>
                Sign Out
              </ListItemText>
            </MenuItem>

            {process.env.REACT_APP_VERSION && (
              <>
                <Divider />
                <Box sx={{ px: 2, py: 0.5 }}>
                  <Typography variant="caption" color="text.disabled">
                    v{process.env.REACT_APP_VERSION}
                    {process.env.REACT_APP_BUILD_HASH
                      ? ` (${process.env.REACT_APP_BUILD_HASH})`
                      : ""}
                  </Typography>
                </Box>
              </>
            )}
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          padding: 2,
          maxWidth: 1200,
          margin: "auto",
        }}
      >
        {children}
      </Box>
    </>
  );
};

export default StudentLayout;
